import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { search } from "../api/searchApi";

function resultLink(result) {
  switch (result.type) {
    case "article":
      return `/articles/${result.id}`;
    case "topic":
      return `/publications/${result.publication_slug}/topics/${result.id}`;
    case "issue_topic":
      return `/issues/${result.issue_id}/topics/${result.id}`;
    default:
      return "/";
  }
}

// Header search — starts as just a button; clicking it expands an input
// (width/opacity transition, see .search-bar--open in App.css) that
// searches titles and authors (بایلاین) across articles, publication
// topics, and issue topics as you type, debounced.
function SearchBar() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  function closeSearch() {
    setOpen(false);
    setQuery("");
    setResults([]);
    setSearched(false);
  }

  useEffect(() => {
    if (!open) return undefined;
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      setSearched(false);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const timeout = setTimeout(() => {
      search(trimmed)
        .then((data) => setResults(data))
        .catch(() => setResults([]))
        .finally(() => {
          setSearched(true);
          setLoading(false);
        });
    }, 300);

    return () => clearTimeout(timeout);
  }, [query]);

  useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        closeSearch();
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") closeSearch();
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const showDropdown = open && query.trim().length > 0;

  return (
    <div className={`search-bar${open ? " search-bar--open" : ""}`} ref={containerRef}>
      <button
        type="button"
        className="search-bar-toggle"
        onClick={() => (open ? closeSearch() : setOpen(true))}
        aria-expanded={open}
        aria-label={open ? "بستن جست‌وجو" : "جست‌وجوی عنوان و نویسنده"}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="10.5" cy="10.5" r="6.5" />
          <path d="M20 20l-4.6-4.6" />
        </svg>
      </button>

      <div className="search-bar-panel">
        <input
          ref={inputRef}
          type="text"
          className="search-bar-input"
          placeholder="جست‌وجوی عنوان یا نویسنده..."
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          tabIndex={open ? 0 : -1}
        />

        {showDropdown && (
          <div className="search-bar-results" role="listbox">
            {loading ? (
              <p className="search-bar-status">در حال جست‌وجو...</p>
            ) : results.length > 0 ? (
              <ul>
                {results.map((result) => (
                  <li key={`${result.type}-${result.id}`}>
                    <Link to={resultLink(result)} className="search-bar-result" onClick={closeSearch}>
                      <span className="search-bar-result-title">{result.title}</span>
                      {result.byline && <span className="search-bar-result-byline">{result.byline}</span>}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              searched && <p className="search-bar-status">نتیجه‌ای یافت نشد.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchBar;
